import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { FileUpload } from 'primereact/fileupload';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import { getDocumentos, addDocumento, deleteDocumento, getArchivoUrl } from '../service/ComunidadService';

const fmtFecha = (d) => d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';

const FORM_EMPTY = { titulo: '', descripcion: '', categoria: '' };

const ComunidadDocumentosBarrio = () => {
    const toast = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const { administracionId, usuarioId, rolId } = useAuth();

    const [documentos, setDocumentos] = useState([]);
    const [loading, setLoading] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);
    const [form, setForm] = useState({ ...FORM_EMPTY });
    const [archivo, setArchivo] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const fetchDocumentos = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await getDocumentos(administracionId, barrio.id);
            setDocumentos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los documentos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, administracionId]);

    useEffect(() => { fetchDocumentos(); }, [fetchDocumentos]);

    const abrirNuevo = () => { setForm({ ...FORM_EMPTY }); setArchivo(null); setModalVisible(true); };

    const guardar = async () => {
        if (!form.titulo || !archivo) {
            toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingresá un título y seleccioná un archivo', life: 3000 });
            return;
        }
        setSubmitting(true);
        try {
            await addDocumento({ ...form, barrioId: barrio.id, administracionId, usuarioId }, archivo);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Documento subido correctamente', life: 3000 });
            setModalVisible(false);
            fetchDocumentos();
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo subir el documento', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    const eliminar = (row) => {
        confirmDialog({
            message: `¿Eliminar el documento "${row.titulo}"?`,
            header: 'Confirmar eliminación',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Eliminar',
            rejectLabel: 'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await deleteDocumento(row.documentoComunidadId, administracionId, usuarioId, rolId);
                    fetchDocumentos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo eliminar el documento', life: 3000 });
                }
            },
        });
    };

    return (
        <>
            <h4>Documentos</h4>
            <br />
            <ConfirmDialog />
            <Toast ref={toast} />
            <LoadingOverlay visible={loading} />

            <div className="flex align-items-center gap-3 mb-3">
                <Button label="Subir Documento" icon="pi pi-upload" className="p-button-sm" onClick={abrirNuevo} />
            </div>

            <div className="grid">
                <div className="col-12">
                    <DataTable value={documentos} paginator rows={15}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay documentos cargados todavía"
                        className="p-datatable-gridlines bg-white">
                        <Column field="titulo" header="Título" sortable />
                        <Column field="categoria" header="Categoría" body={(r) => r.categoria || '—'} style={{ width: '10rem' }} />
                        <Column field="usuarioNombre" header="Subido por" body={(r) => r.usuarioNombre || '—'} style={{ width: '12rem' }} />
                        <Column header="Fecha" body={(r) => fmtFecha(r.fechaAlta)} style={{ width: '9rem' }} />
                        <Column header="Archivo" style={{ width: '8rem' }} body={(r) => (
                            <a href={getArchivoUrl(r.archivoUrl)} target="_blank" rel="noreferrer"><i className="pi pi-file" /> Ver</a>
                        )} />
                        <Column header="Acciones" style={{ width: '6rem', textAlign: 'center' }} body={(row) => (
                            row.usuarioId === usuarioId
                                ? <Button icon="pi pi-trash" className="p-button-rounded p-button-text p-button-sm" severity="danger" tooltip="Eliminar (subido por mí)" onClick={() => eliminar(row)} />
                                : null
                        )} />
                    </DataTable>
                </div>
            </div>

            <Dialog header="Subir Documento" visible={modalVisible} style={{ width: '480px' }} modal
                onHide={() => setModalVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalVisible(false)} disabled={submitting} />
                        <Button label="Subir" icon="pi pi-check" className="p-button-sm" onClick={guardar} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>Título *</label>
                        <InputText value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Categoría</label>
                        <InputText value={form.categoria} onChange={(e) => setForm(f => ({ ...f, categoria: e.target.value }))} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Descripción</label>
                        <InputTextarea value={form.descripcion} onChange={(e) => setForm(f => ({ ...f, descripcion: e.target.value }))} rows={3} className="p-inputtext-sm" />
                    </div>
                    <div className="col-12">
                        <label style={lbl}>Archivo *</label>
                        <FileUpload mode="basic" name="file" accept=".pdf,.jpg,.jpeg,.png" maxFileSize={5000000} chooseLabel="Seleccionar archivo"
                            auto={false} customUpload uploadHandler={() => {}}
                            onSelect={(e) => setArchivo(e.files[0])} onClear={() => setArchivo(null)} />
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default ComunidadDocumentosBarrio;
